import { useEffect, useRef, useState } from "react";
import { motion, useMotionValue, useSpring, useReducedMotion } from "framer-motion";
import { ChevronLeft, ChevronRight, Volume2, VolumeX } from "lucide-react";
import { MetalPanel } from "@/components/MetalPanel";
import { ARTWORKS } from "@/data/artworks";
import { playMetalChime, setChimeMuted } from "@/utils/metalChime";

const useIsMobile = () => {
  const [mobile, setMobile] = useState(() => window.innerWidth < 768);
  useEffect(() => {
    const on = () => setMobile(window.innerWidth < 768);
    window.addEventListener("resize", on);
    return () => window.removeEventListener("resize", on);
  }, []);
  return mobile;
};

export const HeroCarousel = () => {
  const [active, setActive] = useState(0);
  const [hovering, setHovering] = useState(false);
  const [muted, setMuted] = useState(false);
  const reduced = useReducedMotion();
  const mobile = useIsMobile();
  const touchX = useRef(null);
  const didMount = useRef(false);
  const n = ARTWORKS.length;

  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const tiltX = useSpring(my, { stiffness: 60, damping: 18 });
  const tiltY = useSpring(mx, { stiffness: 60, damping: 18 });

  useEffect(() => {
    if (!didMount.current) { didMount.current = true; return; }
    playMetalChime();
  }, [active]);

  const toggleMute = () => {
    setMuted((m) => { setChimeMuted(!m); return !m; });
  };

  useEffect(() => {
    if (hovering) return;
    const t = setInterval(() => setActive((a) => (a + 1) % n), 5000);
    return () => clearInterval(t);
  }, [hovering, n, active]);

  const onMouseMove = (e) => {
    if (reduced || mobile) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const nx = (e.clientX - rect.left) / rect.width - 0.5;
    const ny = (e.clientY - rect.top) / rect.height - 0.5;
    mx.set(nx * 8);
    my.set(ny * -6);
  };

  const prev = () => setActive((a) => (a - 1 + n) % n);
  const next = () => setActive((a) => (a + 1) % n);

  const spacing = mobile ? 170 : 265;
  const depth = mobile ? 140 : 210;

  return (
    <div
      data-testid="hero-carousel"
      className="relative w-full"
      style={{ perspective: 1400 }}
      onMouseMove={onMouseMove}
      onMouseEnter={() => setHovering(true)}
      onMouseLeave={() => { setHovering(false); mx.set(0); my.set(0); }}
      onTouchStart={(e) => { touchX.current = e.touches[0].clientX; }}
      onTouchEnd={(e) => {
        if (touchX.current === null) return;
        const dx = e.changedTouches[0].clientX - touchX.current;
        if (dx > 40) prev();
        else if (dx < -40) next();
        touchX.current = null;
      }}
    >
      <motion.div
        className="relative h-[42vh] min-h-[300px] md:h-[46vh] md:min-h-[380px]"
        style={{ transformStyle: "preserve-3d", rotateX: reduced ? 0 : tiltX, rotateY: reduced ? 0 : tiltY }}
      >
        {ARTWORKS.map((art, idx) => {
          let off = idx - active;
          if (off > n / 2) off -= n;
          if (off < -n / 2) off += n;
          const abs = Math.abs(off);
          const visible = abs <= 2;
          return (
            <motion.div
              key={art.id}
              onClick={() => off !== 0 && setActive(idx)}
              initial={false}
              animate={{
                x: off * spacing,
                z: -abs * depth,
                rotateY: off * -16,
                scale: off === 0 ? 1 : 0.9 - abs * 0.03,
                opacity: visible ? 1 - abs * 0.22 : 0,
                filter: `brightness(${off === 0 ? 1 : 0.55 - abs * 0.08})`,
              }}
              transition={{ duration: reduced ? 0 : 0.85, ease: [0.16, 1, 0.3, 1] }}
              className="absolute left-1/2 top-1/2 w-[52vw] max-w-[230px] md:w-[280px] md:max-w-none -translate-x-1/2 -translate-y-1/2"
              style={{ transformStyle: "preserve-3d", zIndex: 10 - abs, pointerEvents: visible ? "auto" : "none" }}
              data-testid={`hero-panel-${art.id}`}
            >
              <MetalPanel src={art.src} alt={`${art.title} — metal art panel`} />
              <div
                className="absolute -bottom-10 left-1/2 -translate-x-1/2 w-[85%] h-8 rounded-[100%] blur-md"
                style={{ background: "radial-gradient(ellipse, rgba(0,0,0,0.7) 0%, transparent 70%)" }}
              />
              {off === 0 && (
                <motion.div
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.4, duration: 0.6 }}
                  className="absolute -bottom-16 left-1/2 -translate-x-1/2 text-center whitespace-nowrap pointer-events-none"
                >
                  <p className="font-tech text-[10px] tracking-[0.3em] text-white/80 uppercase">{art.title}</p>
                  <p className="text-[9px] tracking-[0.25em] text-cyan-300/70 uppercase mt-1">{art.category}</p>
                </motion.div>
              )}
            </motion.div>
          );
        })}
      </motion.div>

      <div className="absolute inset-y-0 left-2 md:left-8 flex items-center z-20">
        <button
          data-testid="carousel-prev-btn"
          onClick={prev}
          aria-label="Previous artwork"
          className="w-11 h-11 rounded-full border border-white/15 bg-black/40 backdrop-blur-md flex items-center justify-center text-white/70 hover:text-cyan-300 hover:border-cyan-400/40 transition-colors duration-300"
        >
          <ChevronLeft size={18} />
        </button>
      </div>
      <div className="absolute inset-y-0 right-2 md:right-8 flex items-center z-20">
        <button
          data-testid="carousel-next-btn"
          onClick={next}
          aria-label="Next artwork"
          className="w-11 h-11 rounded-full border border-white/15 bg-black/40 backdrop-blur-md flex items-center justify-center text-white/70 hover:text-cyan-300 hover:border-cyan-400/40 transition-colors duration-300"
        >
          <ChevronRight size={18} />
        </button>
      </div>

      <div className="relative z-30 flex items-center justify-center gap-3 mt-20" data-testid="carousel-dots">
        {ARTWORKS.map((a, i) => (
          <button
            key={a.id}
            onClick={() => setActive(i)}
            aria-label={`Go to ${a.title}`}
            data-testid={`carousel-dot-${i}`}
            className={`h-1 rounded-full transition-all duration-500 ${i === active ? "w-8 bg-cyan-300/80" : "w-2 bg-white/20 hover:bg-white/40"}`}
          />
        ))}
        <button
          onClick={toggleMute}
          aria-label={muted ? "Unmute panel chime" : "Mute panel chime"}
          data-testid="carousel-mute-btn"
          className="ml-3 text-white/40 hover:text-cyan-300 transition-colors duration-300"
        >
          {muted ? <VolumeX size={14} /> : <Volume2 size={14} />}
        </button>
      </div>
    </div>
  );
};
